import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const SearchFilterBar = ({
  filters = {},
  categories = [],
  searchQuery = '',
  onSearchChange = () => {},
  onFiltersChange = () => {},
  onSearch = () => {},
  isLoading = false
}) => {
  const [keyword, setKeyword] = useState(searchQuery || '');
  const [localFilters, setLocalFilters] = useState({
    transactionType: filters.transactionType || 'all',
    propertyType: filters.propertyType || 'all',
    location: filters.location || '',
    priceMin: filters.priceRange?.[0] || 0,
    priceMax: filters.priceRange?.[1] || '',
  });

  useEffect(() => {
    setKeyword(searchQuery || '');
  }, [searchQuery]);

  useEffect(() => {
    setLocalFilters({
      transactionType: filters.transactionType || 'all',
      propertyType: filters.propertyType || 'all',
      location: filters.location || '',
      priceMin: filters.priceRange?.[0] || 0,
      priceMax: filters.priceRange?.[1] || '',
    });
  }, [filters]);

  const handleTransactionTypeChange = (transactionType) => {
    const next = { ...localFilters, transactionType };
    setLocalFilters(next);
    onFiltersChange({
      ...filters,
      transactionType,
      propertyType: next.propertyType,
      location: next.location,
      priceRange: [
        next.priceMin || 0,
        next.priceMax || 500000000
      ]
    });
  };

  const handlePropertyTypeChange = (type) => {
    setLocalFilters(prev => ({ ...prev, propertyType: type }));
  };

  const handleLocationChange = (location) => {
    setLocalFilters(prev => ({ ...prev, location }));
  };

  const handlePriceMinChange = (e) => {
    const value = e.target.value === '' ? 0 : parseInt(e.target.value) || 0;
    setLocalFilters(prev => ({ ...prev, priceMin: value }));
  };

  const handlePriceMaxChange = (e) => {
    const value = e.target.value === '' ? '' : parseInt(e.target.value) || 0;
    setLocalFilters(prev => ({ ...prev, priceMax: value }));
  };

  const handleSearch = () => {
    const newFilters = {
      ...filters,
      transactionType: localFilters.transactionType,
      propertyType: localFilters.propertyType,
      location: localFilters.location,
      priceRange: [
        localFilters.priceMin || 0,
        localFilters.priceMax || 500000000
      ]
    };
    onFiltersChange(newFilters);
    onSearchChange(keyword.trim());
    onSearch();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const transactionTypeOptions = [
    { value: 'all', label: 'All' },
    { value: 'sell', label: 'For Sale' },
    { value: 'rent', label: 'For Rent' },
    { value: 'luxury', label: 'Luxury' },
  ];

  const propertyTypeOptions = categories?.length > 0
    ? [{ value: 'all', label: 'All Types' }, ...categories.map(category => ({ value: category.id.toString(), label: category.category }))]
    : [
        { value: 'all', label: 'All Types' },
        { value: 'apartment', label: 'Apartment' },
        { value: 'house', label: 'House' },
        { value: 'land', label: 'Land' },
        { value: 'commercial', label: 'Commercial' },
        { value: 'office', label: 'Office' },
      ];

  const locationOptions = [
    { value: '', label: 'All Regions' },
    // Uganda
    { value: 'Kampala', label: 'Kampala' },
    { value: 'Entebbe', label: 'Entebbe' },
    { value: 'Wakiso', label: 'Wakiso' },
    { value: 'Mukono', label: 'Mukono' },
    { value: 'Jinja', label: 'Jinja' },
    { value: 'Mbale', label: 'Mbale' },
    { value: 'Mbarara', label: 'Mbarara' },
    { value: 'Gulu', label: 'Gulu' },
    { value: 'Lira', label: 'Lira' },
    { value: 'Soroti', label: 'Soroti' },
    { value: 'Fort Portal', label: 'Fort Portal' },
    { value: 'Hoima', label: 'Hoima' },
    { value: 'Kasese', label: 'Kasese' },
    { value: 'Masaka', label: 'Masaka' },
    { value: 'Kabale', label: 'Kabale' },
    { value: 'Arua', label: 'Arua' },
    { value: 'Bushenyi', label: 'Bushenyi' },
    { value: 'Tororo', label: 'Tororo' },
    { value: 'Moroto', label: 'Moroto' },
    { value: 'Mityana', label: 'Mityana' },
    { value: 'Sembabule', label: 'Sembabule' },
    // Kenya
    { value: 'Nairobi', label: 'Nairobi' },
    { value: 'Mombasa', label: 'Mombasa' },
    { value: 'Kisumu', label: 'Kisumu' },
    { value: 'Nakuru', label: 'Nakuru' },
    { value: 'Eldoret', label: 'Eldoret' },
    { value: 'Thika', label: 'Thika' },
    { value: 'Malindi', label: 'Malindi' },
    { value: 'Nyeri', label: 'Nyeri' },
    { value: 'Kitale', label: 'Kitale' },
    { value: 'Machakos', label: 'Machakos' },
    { value: 'Naivasha', label: 'Naivasha' },
    { value: 'Kakamega', label: 'Kakamega' },
    { value: 'Kilifi', label: 'Kilifi' },
    { value: 'Garissa', label: 'Garissa' },
    { value: 'Busia', label: 'Busia' },
    { value: 'Voi', label: 'Voi' },
    { value: 'Nanyuki', label: 'Nanyuki' },
    { value: 'Meru', label: 'Meru' },
    // Tanzania
    { value: 'Dar es Salaam', label: 'Dar es Salaam' },
    { value: 'Arusha', label: 'Arusha' },
    { value: 'Mwanza', label: 'Mwanza' },
    { value: 'Dodoma', label: 'Dodoma' },
    { value: 'Zanzibar', label: 'Zanzibar' },
    { value: 'Mbeya', label: 'Mbeya' },
    { value: 'Morogoro', label: 'Morogoro' },
    { value: 'Tanga', label: 'Tanga' },
    { value: 'Kigoma', label: 'Kigoma' },
    { value: 'Moshi', label: 'Moshi' },
    { value: 'Iringa', label: 'Iringa' },
    { value: 'Tabora', label: 'Tabora' },
    { value: 'Singida', label: 'Singida' },
    { value: 'Shinyanga', label: 'Shinyanga' },
    { value: 'Bukoba', label: 'Bukoba' },
    { value: 'Mtwara', label: 'Mtwara' },
    // Rwanda
    { value: 'Kigali', label: 'Kigali' },
    { value: 'Huye', label: 'Huye' },
    { value: 'Musanze', label: 'Musanze' },
    { value: 'Rubavu', label: 'Rubavu' },
    { value: 'Rusizi', label: 'Rusizi' },
    { value: 'Rwamagana', label: 'Rwamagana' },
    { value: 'Nyagatare', label: 'Nyagatare' },
    // Burundi
    { value: 'Bujumbura', label: 'Bujumbura' },
    { value: 'Gitega', label: 'Gitega' },
    { value: 'Ngozi', label: 'Ngozi' },
    { value: 'Rumonge', label: 'Rumonge' },
    { value: 'Makamba', label: 'Makamba' },
    { value: 'Kirundo', label: 'Kirundo' },
    { value: 'Muyinga', label: 'Muyinga' },
    { value: 'Ruyigi', label: 'Ruyigi' },
    { value: 'Cibitoke', label: 'Cibitoke' },
    // South Sudan
    { value: 'Juba', label: 'Juba' },
    { value: 'Malakal', label: 'Malakal' },
    { value: 'Wau', label: 'Wau' },
    { value: 'Yei', label: 'Yei' },
    { value: 'Torit', label: 'Torit' },
    { value: 'Bor', label: 'Bor' },
    { value: 'Bentiu', label: 'Bentiu' },
    // Somalia
    { value: 'Mogadishu', label: 'Mogadishu' },
    { value: 'Hargeisa', label: 'Hargeisa' },
    { value: 'Garowe', label: 'Garowe' },
    { value: 'Kismayo', label: 'Kismayo' },
    { value: 'Bosaso', label: 'Bosaso' },
    { value: 'Baidoa', label: 'Baidoa' },
    { value: 'Beledweyne', label: 'Beledweyne' },
    { value: 'Galkayo', label: 'Galkayo' },
    { value: 'Berbera', label: 'Berbera' },
    { value: 'Burao', label: 'Burao' },
    // Ethiopia
    { value: 'Addis Ababa', label: 'Addis Ababa' },
    { value: 'Dire Dawa', label: 'Dire Dawa' },
    { value: 'Bahir Dar', label: 'Bahir Dar' },
    { value: 'Harar', label: 'Harar' },
    { value: 'Mekelle', label: 'Mekelle' },
    { value: 'Gondar', label: 'Gondar' },
    { value: 'Hawassa', label: 'Hawassa' },
    { value: 'Jimma', label: 'Jimma' },
    { value: 'Adama', label: 'Adama' },
    { value: 'Dessie', label: 'Dessie' },
    { value: 'Nekemte', label: 'Nekemte' },
    { value: 'Jigjiga', label: 'Jigjiga' },
    { value: 'Dilla', label: 'Dilla' },
    // Djibouti + Eritrea
    { value: 'Djibouti', label: 'Djibouti' },
    { value: 'Asmara', label: 'Asmara' },
    { value: 'Massawa', label: 'Massawa' },
    { value: 'Keren', label: 'Keren' },
  ];

  return (
    <div className="w-full bg-white rounded-lg shadow-lg border border-gray-100">
      {/* Property Type Tabs */}
      <div className="flex border-b border-gray-200">
        {transactionTypeOptions.map((option) => (
          <button
            key={option.value}
            onClick={() => handleTransactionTypeChange(option.value)}
            className={`flex-1 px-4 py-3 font-medium text-sm transition-all duration-200 border-b-2 ${
              localFilters.transactionType === option.value
                ? 'text-primary border-b-primary bg-primary/5'
                : 'text-gray-600 border-b-transparent hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>

      {/* Filter Controls */}
      <div className="p-6 space-y-4">
        <div className="flex flex-col">
          <label className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">
            Search
          </label>
          <div className="relative">
            <Icon
              name="Search"
              size={16}
              className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
            />
            <input
              type="text"
              value={keyword}
              onChange={(e) => {
                const value = e.target.value;
                setKeyword(value);
                onSearchChange(value);
              }}
              onKeyPress={handleKeyPress}
              placeholder='Type a word, e.g. Kampala'
              className="w-full h-11 rounded-lg border border-slate-200 bg-white pl-10 pr-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-300"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[1.85fr_1fr_1fr_1fr_0.95fr] gap-3 items-end">
          {/* Region Select */}
          <div className="flex flex-col">
            <label className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">Region</label>
            <Select
              value={localFilters.location}
              onChange={handleLocationChange}
              options={locationOptions}
              searchable
              placeholder="Select a region"
              className="w-full"
            />
          </div>

          {/* Property Type Select */}
          <div className="flex flex-col">
            <label className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">Property Type</label>
            <Select
              value={localFilters.propertyType}
              onChange={handlePropertyTypeChange}
              options={propertyTypeOptions}
              searchable
              className="w-full"
            />
          </div>

          {/* Min Price */}
          <div className="flex flex-col">
            <label className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">Min Price</label>
            <Input
              type="number"
              placeholder="0"
              value={localFilters.priceMin}
              onChange={handlePriceMinChange}
              onKeyPress={handleKeyPress}
              className="w-full h-11 rounded-lg"
              min="0"
            />
          </div>

          {/* Max Price */}
          <div className="flex flex-col">
            <label className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">Max Price</label>
            <Input
              type="number"
              placeholder="No limit"
              value={localFilters.priceMax}
              onChange={handlePriceMaxChange}
              onKeyPress={handleKeyPress}
              className="w-full h-11 rounded-lg"
              min="0"
            />
          </div>

          {/* Search Button */}
          <div className="flex flex-col lg:items-end">
            <Button
              onClick={handleSearch}
              disabled={isLoading}
              className="w-full lg:w-auto h-11 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-all duration-200 flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <Icon name="Loader" size={16} className="animate-spin" />
                  <span className="hidden sm:inline">Search</span>
                </>
              ) : (
                <>
                  <Icon name="Search" size={16} />
                  <span className="hidden sm:inline">Search</span>
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchFilterBar;
